// Vérification de compromission en k-anonymity (provider actif V1 : Have I Been Pwned).
// Le mot de passe et son empreinte complète ne quittent JAMAIS le navigateur : seul le préfixe
// SHA-1 de 5 caractères est transmis ; la comparaison du suffixe est faite localement.
// SHA-1 sert UNIQUEMENT d'index de recherche du corpus Pwned Passwords (pas de stockage ni de dérivation).
export const PREFIX_LENGTH = 5;

export async function sha1HexUpper(str) {
  const buf = await crypto.subtle.digest("SHA-1", new TextEncoder().encode(str));
  return Array.from(new Uint8Array(buf)).map((b) => b.toString(16).padStart(2, "0")).join("").toUpperCase();
}

// Réponse « range » (SUFFIXE:COMPTE par ligne) -> nombre d'occurrences du suffixe (0 si absent / padding).
export function countInRange(text, suffix) {
  for (const line of String(text).split("\n")) {
    const [suf, count] = line.trim().split(":");
    if (suf && suf.toUpperCase() === suffix) return parseInt(count, 10) || 0;
  }
  return 0;
}

const HIBP_LOCAL = process.env.REACT_APP_HIBP_LOCAL_RANGE_URL; // ex. https://…/range (non configuré => désactivé)

const FETCH_OPTS = { credentials: "omit", referrerPolicy: "no-referrer", cache: "no-store" };

async function rangeFetch(url, headers) {
  const res = await fetch(url, { ...FETCH_OPTS, headers });
  if (!res.ok) throw new Error(`range ${res.status}`);
  return res.text();
}

const hibpLookup = async (url, suffix) => {
  const count = countInRange(await rangeFetch(url, { "Add-Padding": "true" }), suffix);
  return { found: count > 0, count };
};

// Registre des providers (architecture multi-provider ; SpyCloud suspendu, non enregistré). Chacun : { id, group, isEnabled(), lookup(prefix, suffix) -> {found, count} }.
// Seul `prefix` (5 caractères) est transmis ; `suffix` sert à la comparaison LOCALE.
// group : providers interchangeables du même corpus (un seul utilisé, par ordre de priorité).
export const BREACH_PROVIDERS = [
  {
    id: "hibp_local", group: "hibp", priority: 0,
    isEnabled: async () => !!HIBP_LOCAL,
    lookup: (p, suffix) => hibpLookup(`${HIBP_LOCAL}/${p}`, suffix),
  },
  {
    id: "hibp", group: "hibp", priority: 1,
    isEnabled: async () => true,
    lookup: (p, suffix) => hibpLookup(`https://api.pwnedpasswords.com/range/${p}`, suffix),
  },
];

// Sélectionne, par corpus, le premier provider actif (priorité) ; fallback sur le suivant en cas d'erreur.
async function checkGroup(providers, prefix, suffix) {
  let last = null;
  for (const p of providers) {
    try {
      const { found, count } = await p.lookup(prefix, suffix);
      return { provider: p.id, status: "ok", found, count, checkedAt: new Date().toISOString() };
    } catch (e) {
      last = { provider: p.id, status: "error", found: false, count: 0, checkedAt: new Date().toISOString() };
    }
  }
  return last;
}
// Agrégation : 1 occurrence dans 1 source => compromis. Une erreur n'est JAMAIS un « non compromis ».
// Les comptes ne sont jamais additionnés (corpus qui se chevauchent).
export function aggregate(results) {
  const ok = results.filter((r) => r.status === "ok");
  const status = results.some((r) => r.found) ? "compromised"
    : ok.length === 0 ? "unavailable"
      : ok.length < results.length ? "partial" : "clean";
  return { status, results, consulted: ok.length, total: results.length };
}

// Sources effectivement actives (un provider par corpus) : seules celles-ci sont mentionnées à l'utilisateur.
export async function activeBreachSources(providers = BREACH_PROVIDERS) {
  const seen = {};
  for (const p of [...providers].sort((a, b) => a.priority - b.priority)) {
    if (!seen[p.group] && await p.isEnabled()) seen[p.group] = p.id;
  }
  return Object.values(seen);
}

export async function checkBreaches(password, providers = BREACH_PROVIDERS) {
  const hash = await sha1HexUpper(password);
  const prefix = hash.slice(0, PREFIX_LENGTH);
  const suffix = hash.slice(PREFIX_LENGTH);
  const enabled = [];
  for (const p of providers) if (await p.isEnabled()) enabled.push(p);
  const groups = {};
  enabled.sort((a, b) => a.priority - b.priority).forEach((p) => { (groups[p.group] = groups[p.group] || []).push(p); });
  const results = await Promise.all(Object.values(groups).map((g) => checkGroup(g, prefix, suffix)));
  return aggregate(results);
}
